import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import KycRequiredBlurCard from '../../../../components/Cards/KycRequiredBlurCard';
import BarChart from '../../../../components/Charts/BarChart';
import ChartFilter from '../../../../components/Charts/ChartFilter/ChartFilter';
import PerformanceHistoryLegend from '../../../../components/Charts/CustomLegends/PerformanceHistoryLegend';
import PerformanceHistoryTooltip from '../../../../components/Charts/CustomToolTips/PerformanceHistoryTooltip';
import usePerformanceInsight from '../../../../hooks/dashboard/driver-list/usePerformanceInsight';

const PerformanceHistory = ({ baseUrl }: { baseUrl: string }) => {
  const [dataUrl, setDataUrl] = useState<string>(baseUrl);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 884);

  const { getPerformanceDataInsight, PerformanceInsightData } =
    usePerformanceInsight();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 884);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!dataUrl) return;
    if (baseUrl !== dataUrl) {
      getPerformanceDataInsight(dataUrl);
    }
    // eslint-disable-next-line
  }, [dataUrl]);

  return (
    <KycRequiredBlurCard message='Complete KYC to Access Performance Metrics'>
      <Container>
        <HeaderArea>
          <Header>Performance History</Header>

          <ControlArea>
            <ChartFilter
              setDataUrl={setDataUrl}
              dataUrl={dataUrl}
              variant='year'
            />
          </ControlArea>
        </HeaderArea>

        <ChartArea>
          <BarChart
            xAxisDataKey='period'
            data={PerformanceInsightData}
            isAnimationActive={true}
            dataKeys={['weightedScore']}
            fills={['#5B91F5']}
            showLegend={false}
            aspect={isMobile ? 1.5 : 3}
            minHeight={isMobile ? 220 : undefined}
            tooltipLabel={['Performance Score']}
            externalTooltip={{
              content: (
                <PerformanceHistoryTooltip tooltipLabel={['Average Weight']} />
              ),
              cursor: { fill: 'rgba(0, 0, 0, 0.05)' },
            }}
            yLabel='Weighted average per day (%)'
          />

          <PerformanceHistoryLegend label='Days in the month' />
        </ChartArea>
      </Container>
    </KycRequiredBlurCard>
  );
};

export default PerformanceHistory;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 24px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
`;

const ChartArea = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

const ControlArea = styled.div`
  display: flex;
`;

const HeaderArea = styled.div`
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 18px;

  @media (max-width: 884px) {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 18px;
`;
